import { BellIcon } from "@/components/icons/Bell";
import clsx from "clsx";
import type { PDPProduct } from "../../page";
import type { Sizes } from "../ActionSection/ActionSection";
import styles from "./jeans-size-selector.module.css";

const JeansSizeSelector = ({
  jeansHook,
  regularSizes,
  comingSoon,
  product,
  showSizeGuide = true,
  onNotifyClick,
  className,
}: {
  jeansHook: ReturnType<typeof import("../../hooks/useJeansSizes").default>;
  regularSizes: Sizes;
  comingSoon: boolean;
  product: PDPProduct;
  showSizeGuide?: boolean;
  onNotifyClick?: () => void;
  className?: string;
}) => {
  const { selection, selectWidth, selectLength, getSizeMatrix } = jeansHook;
  const sizeMatrix = getSizeMatrix();

  const allWidths = Object.keys(sizeMatrix).sort(
    (a, b) => parseInt(a) - parseInt(b)
  );
  const allLengths = selection.width
    ? Object.keys(sizeMatrix[selection.width] || {}).sort(
        (a, b) => parseInt(a) - parseInt(b)
      )
    : Array.from(
        new Set(
          Object.values(sizeMatrix).flatMap((widthObj) => Object.keys(widthObj))
        )
      ).sort((a, b) => parseInt(a) - parseInt(b));

  const getWidthStockStatus = (width: string) => {
    const widthSizes = sizeMatrix[width] || {};
    const availableLengths = Object.values(widthSizes);

    const inStockCount = availableLengths.filter(
      (size) => size.stock && !comingSoon
    ).length;
    const comingSoonCount = comingSoon
      ? availableLengths.filter((size) => size.stock || comingSoon).length
      : 0;
    const totalCount = availableLengths.length;

    return {
      allOutOfStock: inStockCount === 0 && comingSoonCount === 0,
      partialStock:
        (inStockCount > 0 || comingSoonCount > 0) &&
        inStockCount + comingSoonCount < totalCount,
      allInStock: inStockCount === totalCount && totalCount > 0 && !comingSoon,
      allComingSoon:
        comingSoon && comingSoonCount === totalCount && totalCount > 0,
    };
  };

  const handleWidthClick = (width: string) => {
    const stockStatus = getWidthStockStatus(width);

    if (stockStatus.allOutOfStock) {
      onNotifyClick?.();
      return;
    }

    selectWidth(width);
  };

  const handleLengthClick = (length: string) => {
    if (!selection.width) return;

    const jeansSize = sizeMatrix[selection.width][length];
    if (!jeansSize) return;

    const isInStock = jeansSize.stock && !comingSoon;
    const isComingSoon = comingSoon && (jeansSize.stock || comingSoon);

    if (!isInStock && !isComingSoon) {
      selectLength(length);
      onNotifyClick?.();
      return;
    }
    selectLength(length);
  };

  return (
    <div className={styles.jeansSizeSelector}>
      <div className={styles.dimensionGroup}>
        <label className={styles.dimensionLabel}>Width</label>
        <div className={styles.toggleGroup}>
          {allWidths.map((width) => {
            const stockStatus = getWidthStockStatus(width);

            return (
              <button
                key={width}
                type="button"
                className={clsx(styles.toggle, className, {
                  [styles.toggleSelected]: selection.width === width,
                  [styles.toggleOutOfStock]: stockStatus.allOutOfStock,
                  [styles.toggleComingSoon]: comingSoon,
                })}
                onClick={comingSoon ? undefined : () => handleWidthClick(width)}
                disabled={comingSoon}
              >
                {width}
                {stockStatus.allOutOfStock && (
                  <span className={styles.outOfStockIndicator}>!</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.dimensionGroup}>
        <label className={styles.dimensionLabel}>Length</label>
        <div className={styles.toggleGroup}>
          {allLengths.map((length) => {
            if (!selection.width) {
              return (
                <button
                  key={length}
                  type="button"
                  className={clsx(styles.toggle, className, {
                    [styles.toggleComingSoon]: comingSoon,
                  })}
                  disabled={true}
                >
                  {length}
                </button>
              );
            }

            const jeansSize = sizeMatrix[selection.width][length];
            if (!jeansSize) {
              return (
                <button
                  key={length}
                  type="button"
                  className={clsx(styles.toggle, className, {
                    [styles.toggleComingSoon]: comingSoon,
                  })}
                  disabled={true}
                >
                  {length}
                </button>
              );
            }

            const isInStock = jeansSize.stock && !comingSoon;
            const isComingSoon = comingSoon;

            return (
              <button
                key={length}
                type="button"
                className={clsx(styles.toggle, className, {
                  [styles.toggleSelected]: selection.length === length,
                  [styles.toggleLowStock]:
                    jeansSize.fewLeft && jeansSize.stock && !comingSoon,
                  [styles.toggleOutOfStock]: !isInStock && !isComingSoon,
                  [styles.toggleComingSoon]: comingSoon,
                })}
                onClick={
                  comingSoon ? undefined : () => handleLengthClick(length)
                }
                disabled={comingSoon}
              >
                {jeansSize.fewLeft && jeansSize.stock && !comingSoon && (
                  <span className={styles.fewLeftDot}></span>
                )}
                {!isInStock && !isComingSoon && (
                  <span className={styles.outOfStockBellIcon}>
                    <BellIcon />
                  </span>
                )}
                {length}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JeansSizeSelector;
